import React, { useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";

import { useGetTransitionDetailsDto } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitionDetailsDto";

import { useRetry } from "../../hooks/useRetry";
import TransitionErrorState from "../../ui/TransitionErrorState";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import {
  ProcessDetail,
  TransitionDetailItem,
  TransitionDetailsProps,
} from "./types";

import styles from "./TransitionDetails.module.css";

const createTransitionLabel = (
  custodian?: string,
  transitionType?: string
): string => {
  if (custodian && transitionType) {
    return `${custodian} ${transitionType}`;
  }
  return transitionType || "--";
};

const createProcessLabel = (
  custodian?: string,
  transitionType?: string
): string => {
  const baseLabel = createTransitionLabel(custodian, transitionType);
  return `${baseLabel} Process for Clients`;
};

const DocumentsList: React.FC<{
  documents: Array<{ name: string; additionalInfo?: string }>;
}> = ({ documents }) => {
  if (!documents || documents.length === 0) {
    return (
      <Text variant="body" color="textSubtle">
        No documents available
      </Text>
    );
  }

  return (
    <div className={styles.documentsList}>
      {documents.map((doc, index) => (
        <Text key={index} variant="body" color="text">
          {doc.name}
        </Text>
      ))}
    </div>
  );
};

const TransitionDetailsCard: React.FC<TransitionDetailsProps> = ({
  transitionId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const auth = useAdvisorRequestAuth();
  const { handleRetry } = useRetry("transitions", transitionId);
  const {
    data: transitionDetails,
    isLoading,
    hasError,
  } = useGetTransitionDetailsDto(transitionId, auth);

  const details: TransitionDetailItem[] = [
    {
      label: "Transition Type",
      value: createTransitionLabel(
        transitionDetails?.custodian || undefined,
        transitionDetails?.transitionType || undefined
      ),
      isEmpty:
        !transitionDetails?.custodian && !transitionDetails?.transitionType,
    },
    {
      label: "Prior Firm",
      value: transitionDetails?.priorFirm || null,
      isEmpty: !transitionDetails?.priorFirm,
    },
    {
      label: "Custodian Rep Code",
      value: transitionDetails?.custodianRepCode || null,
      isEmpty: !transitionDetails?.custodianRepCode,
    },
  ];

  if (isLoading) {
    return (
      <div className={`${styles.container} ${styles.skeletonContainer}`}>
        {Array.from({ length: 3 }, (_, index) => (
          <div
            key={`skeleton-${index}`}
            className={toClassName(styles.detailRow, styles.skeleton)}
          >
            <Skeleton variant="rounded" width="40%" height={16} />
            <Skeleton variant="rounded" width="70%" height={16} />
          </div>
        ))}
      </div>
    );
  }

  if (hasError) {
    return (
      <TransitionErrorState
        message="Could not load transition details"
        onRetry={handleRetry}
      />
    );
  }

  const processDetails: ProcessDetail[] = [
    {
      label: "Forms sent to client",
      subLabel: "Clients will receive an email with these documents to sign",
      component: (
        <DocumentsList
          documents={transitionDetails?.documents?.documentsToSign || []}
        />
      ),
      isSpecial: true,
    },
    {
      label: "Account Numbers",
      value: transitionDetails?.documents?.accountNumbers,
    },
    {
      label: "Signature Method",
      value: transitionDetails?.documents?.signatureMethod,
    },
    {
      label: "Standing Instructions",
      value: transitionDetails?.documents?.standingInstructions,
    },
  ];

  // Check if we have any process details data to show in the accordion
  const hasProcessData =
    (transitionDetails?.documents?.documentsToSign &&
      transitionDetails.documents.documentsToSign.length > 0) ||
    transitionDetails?.documents?.accountNumbers ||
    transitionDetails?.documents?.signatureMethod ||
    transitionDetails?.documents?.standingInstructions;

  return (
    <div className={styles.container}>
      {details.map((detail, index) => (
        <div key={index} className={styles.detailRow}>
          <Text variant="heading1" color="text">
            {detail.label}
          </Text>
          <Text variant="body" color={detail.isEmpty ? "textSubtle" : "text"}>
            {detail.value || "--"}
          </Text>
        </div>
      ))}

      {hasProcessData && (
        <div className={styles.accordionContainer}>
          <Accordion
            expanded={isExpanded}
            onChange={() => setIsExpanded(!isExpanded)}
            className={styles.accordion}
            elevation={0}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon className={styles.expandIcon} />}
              className={styles.accordionSummary}
              sx={{
                flexDirection: "row-reverse",
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  marginLeft: 0,
                  marginRight: "8px",
                },
              }}
            >
              <Text variant="heading1" color="text">
                {createProcessLabel(
                  transitionDetails?.custodian || undefined,
                  transitionDetails?.transitionType || undefined
                )}
              </Text>
            </AccordionSummary>
            <AccordionDetails className={styles.accordionDetails}>
              {processDetails.map((detail, index) => (
                <div key={index} className={styles.accordionRow}>
                  <div>
                    <Text variant="body" color="text">
                      {detail.label}
                    </Text>
                    {detail.subLabel && (
                      <Text variant="bodySmall" color="textSubtle">
                        {detail.subLabel}
                      </Text>
                    )}
                  </div>
                  <div className={styles.value}>
                    {detail.component || (
                      <Text variant="body" color="text">
                        {detail.value || "--"}
                      </Text>
                    )}
                  </div>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default TransitionDetailsCard;
